import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";
import api from "@/lib/api";
import { SBGameLines, SBColumnHeader, leagueLabel } from "@/components/sportsbook/SBBoardGame";
import { SBTag } from "@/components/sportsbook/sb";
import { formatOdds } from "@/lib/odds";
import { sortSportsbookEvents } from "@/lib/sportsbookPriority";
import { openCookiePreferences } from "@/lib/cookieConsent";
import logoWordmark from "../assets/pnrbet-wordmark.png";
import logoStacked from "../assets/pnrbet-stacked.png";
import pnrIcon from "../assets/pnrbet-icon.png";

const FEATURES = [
  { icon: "verified", title: "Provably fair", sub: "Verifiable game outcomes" },
  { icon: "pnrcoin", title: "Free daily coins", sub: "500 PCoins on sign-up" },
  { icon: "sportsbook", title: "Real lines", sub: "Sports & esports markets" },
  { icon: "wallet", title: "No card required", sub: "No purchase necessary" },
];

const ORIGINALS = [
  { icon: "originals", name: "Crash", sub: "Cash out before it breaks." },
  { icon: "dice", name: "Dice", sub: "Set the line. Roll it." },
  { icon: "mines", name: "Mines", sub: "Find value. Avoid the mine." },
  { icon: "blackjack", name: "Blackjack", sub: "Beat the dealer to 21." },
  { icon: "slots", name: "Slots", sub: "Spin the reels for a line." },
  { icon: "roulette", name: "Roulette", sub: "Pick your number or your side." },
  { icon: "coinflip", name: "Coin Flip", sub: "Heads or tails. Double or nothing." },
  { icon: "baccarat", name: "Baccarat", sub: "Player, banker or tie." },
];

const STEPS = [
  { n: "01", title: "Create your account", sub: "Sign up free. No purchase or card is required to start." },
  { n: "02", title: "Choose your action", sub: "Build a sports or esports slip, or jump into a provably-fair original." },
  { n: "03", title: "Keep the balance moving", sub: "Winning picks settle to your coin balance. Return daily for another free drop." },
];

const FAQS = [
  { q: "Is PRYMEBET really free?", a: "Yes. PRYMEBET is a free-to-play social casino. You receive free PCoins on sign-up and more through daily drops. No purchase is necessary to play." },
  { q: "What can I make predictions on?", a: "The platform is built around sports and esports markets, including major leagues and competitive gaming titles, with both single picks and multi-leg parlays." },
  { q: "What does “provably fair” mean?", a: "Provably-fair games use cryptographic methods that let you verify each game outcome rather than simply trusting a black box." },
  { q: "How do I get more coins?", a: "Claim the free daily coin drop and grow your balance through successful predictions and original-game outcomes." },
  { q: "Who can play?", a: "PRYMEBET is intended for eligible players 18 and over. Play responsibly and review the platform’s official rules and eligibility before participating." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Real sportsbook lines (public /events board) — a small, live preview rendered
  // with the exact same component the sportsbook uses, so the design matches 1:1.
  const [board, setBoard] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/events")
      .then(({ data }) => {
        if (!alive) return;
        const rows = sortSportsbookEvents((Array.isArray(data) ? data : [])
          .filter((e) => (e.status === "open" || e.status === "live")
            && e.away_team && e.home_team
            && (e.outcomes || []).some((o) => o.market === "moneyline")))
          .slice(0, 3); // premium leagues first (P0-A), then live-before-upcoming + chronology
        setBoard(rows);
      })
      .catch(() => { if (alive) setBoard([]); });
    return () => { alive = false; };
  }, []);
  // Logged-out acquisition CTAs ("Play free", "Claim 500 free coins", etc.) carry
  // register intent so Login opens on the create-account step; "Log in" links stay plain.
  const primaryTo = user ? "/sportsbook" : "/login?mode=register";
  const [openFaq, setOpenFaq] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const reduce = useReducedMotion();
  // On-mount reveal (not scroll-gated) so crawlers, social bots & print see content by default; disabled for reduced-motion.
  const fadeUp = reduce ? {} : { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };
  const nav = [
    { label: "Sportsbook", href: "#sportsbook" },
    { label: "Originals", href: "#originals" },
    { label: "Rewards", href: "#rewards" },
    { label: "FAQ", href: "#faq" },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#0A0A0A] text-white antialiased">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center">
            <img src={logoWordmark} alt="PRYMEBET" className="h-8 w-auto sm:h-9" />
          </Link>
          <nav className="hidden items-center gap-8 md:flex">
            {nav.map((n) => (
              <a key={n.label} href={n.href} className="text-sm font-medium text-[#a1a1aa] transition-colors hover:text-white">{n.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/sportsbook"><Button data-testid="landing-enter" className="rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">Enter lobby <Icon name="arrow-right" size={16} className="ml-1" /></Button></Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block"><Button variant="ghost" className="text-white hover:bg-[#00B829]/10 hover:text-white">Log in</Button></Link>
                <Link to="/login?mode=register"><Button data-testid="landing-signup" className="rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">Play free</Button></Link>
              </>
            )}
            <button data-testid="mobile-menu-toggle" onClick={() => setMenuOpen((v) => !v)} aria-label="Toggle menu" aria-expanded={menuOpen}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-white md:hidden">
              {menuOpen ? <Icon name="x" size={20} /> : <Icon name="menu" size={20} />}
            </button>
          </div>
        </div>
        {/* Mobile nav dropdown */}
        {menuOpen && (
          <nav data-testid="mobile-nav" className="border-t border-white/10 bg-[#0A0A0A] px-4 py-3 md:hidden">
            <div className="flex flex-col gap-1">
              {nav.map((n) => (
                <a key={n.label} href={n.href} onClick={() => setMenuOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-sm font-semibold text-[#a1a1aa] transition-colors hover:bg-[#00B829]/5 hover:text-white">{n.label}</a>
              ))}
              {!user && (
                <Link to="/login" onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#00B829]/5">Log in</Link>
              )}
            </div>
          </nav>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#00D632]/15 blur-[130px]" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <div className="flex min-w-0 flex-col justify-center">
            <span className="mb-5 w-fit rounded-full border border-[#27272A] bg-[#141417] px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#a1a1aa]">Sports · Esports · Originals</span>
            <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
              Pick the action.<br /><span className="text-[#00D632]">Play for free.</span>
            </h1>
            <p className="mt-5 max-w-lg text-base text-[#a1a1aa] sm:text-lg">
              Real sports and esports lines, fast-settling prediction slips, and provably-fair originals — all powered by free PCoins. No purchase necessary.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={primaryTo}><Button data-testid="hero-cta" className="rounded-full bg-[#00D632] px-8 py-6 text-base font-bold text-[#0A0A0A] hover:bg-[#00B829]">{user ? "Enter the lobby" : "Claim 500 free coins"} <Icon name="arrow-right" size={20} className="ml-1" /></Button></Link>
              <a href="#sportsbook"><Button variant="outline" className="rounded-full border-white/20 bg-white/5 px-8 py-6 text-base font-bold text-white hover:bg-[#00B829]/10 hover:text-white">See how it works</Button></a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#a1a1aa]">
              {["18+ only", "No card required", "Free daily coin drops"].map((t) => (
                <span key={t} className="flex items-center gap-1.5"><Icon name="check" size={16} className="text-[#00D632]" /> {t}</span>
              ))}
            </div>
          </div>

          {/* App-preview phone mockup (design LH-01) */}
          <motion.div {...fadeUp} className="relative flex min-h-[440px] min-w-0 items-center justify-center">
            {/* single app-preview phone — casino / crash, clean & upright (matches the design mockup) */}
            <div className="relative w-[320px] max-w-full rounded-[44px] border border-[#2A2A30] bg-[#0A0A0A] p-2.5 shadow-[0_40px_120px_-30px_rgba(0,214,50,0.18),0_30px_80px_-24px_rgba(0,0,0,0.95)]">
              <div className="relative overflow-hidden rounded-[30px] bg-[#0A0A0A]">
                {/* mini top bar */}
                <div className="flex items-center justify-between border-b border-[#1F1F23] px-3 pb-2 pt-3">
                  <img src={pnrIcon} alt="" className="h-7 w-7" />
                  <div className="flex items-center gap-1.5 rounded-[12px] bg-gradient-to-b from-[#1A1A1E] to-[#121215] px-2 py-1 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08),inset_0_1px_0_rgba(255,255,255,0.06)]">
                    <Icon name="pnrcoin" size={14} className="text-[#00D632]" />
                    <span className="font-display text-[12px] font-extrabold tabular-nums text-white">33,500</span>
                    <Icon name="chevron-down" size={9} className="text-[#71717a]" />
                    <span className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-[7px] bg-[#00D632] shadow-[inset_0_1px_0_rgba(255,255,255,0.32)]"><Icon name="plus" size={11} className="text-[#0A0A0A]" /></span>
                  </div>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#26262B] text-[10px] font-extrabold text-[#E4E4E7] ring-2 ring-[#0A0A0A]">PN</span>
                </div>
                {/* crash view — live round, built off the design-tool mockup with our real assets */}
                <div className="space-y-2.5 p-3">
                  <div className="relative overflow-hidden rounded-2xl border border-[#1F1F23] bg-[#0C0C0E] p-3.5">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#00D632]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#00D632] shadow-[0_0_6px_#00D632]" /> Crash · Live
                      </span>
                      <span className="text-[10px] font-medium tabular-nums text-[#71717a]">Round 48,211</span>
                    </div>
                    <div className="relative mt-1 h-28">
                      <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.035) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.035) 1px,transparent 1px)", backgroundSize: "24px 22px" }} />
                      <svg viewBox="0 0 260 112" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
                        <defs>
                          <linearGradient id="crashFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#00D632" stopOpacity="0.38" />
                            <stop offset="100%" stopColor="#00D632" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        <path d="M0 112 C70 110 150 98 212 36 L212 112 Z" fill="url(#crashFill)" />
                        <path d="M0 112 C70 110 150 98 212 36" fill="none" stroke="#00D632" strokeWidth="2.5" strokeLinecap="round" />
                        <circle cx="212" cy="36" r="8" fill="#00D632" opacity="0.25" />
                        <circle cx="212" cy="36" r="4.5" fill="#00D632" />
                      </svg>
                      <div className="absolute left-2 top-1 font-display text-[40px] font-black leading-none tracking-tighter">12.40<span className="text-[#00D632]">×</span></div>
                    </div>
                    <div className="mt-1.5 flex gap-1.5">
                      {[["1.24×", false], ["3.81×", true], ["1.02×", false], ["7.66×", true], ["2.10×", true], ["1.37×", false]].map(([v, hot], i) => (
                        <span key={i} className={`flex-1 rounded-md py-1 text-center text-[9px] font-black tabular-nums ${hot ? "bg-[#00D632]/14 text-[#00D632]" : "bg-[#141417] text-[#a1a1aa]"}`}>{v}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-2xl border border-[#00D632]/25 bg-[#00D632]/[0.06] px-3 py-2.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D632]"><Icon name="check" size={16} className="text-[#0A0A0A]" /></span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#00D632]">You cashed out</div>
                      <div className="font-display text-xl font-black leading-tight">+31,000</div>
                    </div>
                    <div className="shrink-0 text-right text-[9px] leading-tight text-[#a1a1aa]">
                      <div className="font-bold text-[#e4e4e7]">2,500 bet</div>
                      <div>at 12.40×</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-0.5">
                    <span className="font-display text-[13px] font-extrabold">More Originals</span>
                    <span className="text-[10px] font-semibold text-[#00D632]">See all</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[["mines", "Mines", "862", "from-[#1d4ed8] to-[#0ea5e9]"], ["plinko", "Plinko", "540", "from-[#ea580c] to-[#f59e0b]"], ["dice", "Dice", "1,031", "from-[#7c3aed] to-[#a855f7]"]].map(([ic, name, n, grad]) => (
                      <div key={ic} className="overflow-hidden rounded-xl border border-[#1F1F23] bg-[#0F0F12]">
                        <div className={`flex aspect-[4/3] items-center justify-center bg-gradient-to-br ${grad}`}>
                          <Icon name={ic} size={24} className="text-white" />
                        </div>
                        <div className="px-1.5 py-1.5">
                          <div className="truncate text-[9px] font-bold text-[#e4e4e7]">{name}</div>
                          <div className="text-[8px] font-semibold uppercase tracking-wide text-[#71717a]">{n} playing</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* mini tab bar */}
                <div className="grid grid-cols-5 border-t border-[#1F1F23] px-1 py-1.5">
                  {[["home", "Home"], ["casino", "Casino"], ["sportsbook", "Sports"], ["mybets", "My Bets"], ["menu", "Menu"]].map(([ic, label]) => {
                    const on = ic === "casino";
                    return (
                      <div key={ic} className="flex flex-col items-center gap-0.5">
                        <Icon name={on ? "casino-fill" : ic} size={16} className={on ? "text-[#00D632]" : "text-[#71717A]"} />
                        <span className={`text-[8px] ${on ? "font-bold text-white" : "font-semibold text-[#71717A]"}`}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="border-y border-white/10 bg-[#0C0C0E]">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px px-4 py-10 sm:px-6 lg:grid-cols-4">
          {FEATURES.map((f) => {
            const iconName = f.icon;
            return (
              <div key={f.title} className="flex flex-col items-start gap-2 px-2 py-2">
                <Icon name={iconName} size={20} className="text-[#00D632]" />
                <p className="font-display text-sm font-bold">{f.title}</p>
                <p className="text-xs text-[#a1a1aa]">{f.sub}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* 01 The platform */}
      <section id="sportsbook" className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <motion.div {...fadeUp}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#00D632]">01 / The platform</p>
          <h2 className="max-w-2xl font-display text-3xl font-black tracking-tight sm:text-4xl">One balance. Two ways to stay in the action.</h2>
          <p className="mt-4 max-w-2xl text-base text-[#a1a1aa]">Build prediction slips from real sports and esports lines, or jump into fast originals when you want instant action. Same account. Same free coin balance.</p>
        </motion.div>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <motion.div {...fadeUp} className="rounded-2xl border border-[#27272A] bg-[#141417] p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#a1a1aa]">Sportsbook / Predictions</p>
            <h3 className="mt-2 font-display text-xl font-black tracking-tight">Follow the games you already watch.</h3>
            <p className="mt-2 text-sm text-[#a1a1aa]">Single picks, parlays, live markets and quick settlement across major sports and esports.</p>
            <div className="mt-4 space-y-2">
              {board.length > 0 ? (
                <div className="overflow-hidden rounded-xl border border-[#1F1F23] bg-[#0F0F12] pt-2">
                  <SBColumnHeader compact />
                  {board.map((e, i) => (
                    <div key={e.id} className={i > 0 ? "border-t border-[#1F1F23] pt-2" : ""}>
                      <div className="flex items-center justify-between px-3 pb-0.5 pt-1.5">
                        <span className="truncate text-[10px] font-bold uppercase tracking-wider text-[#a1a1aa]">{leagueLabel(e.league) || e.sport}</span>
                        {e.status === "live" && <SBTag live>Live</SBTag>}
                      </div>
                      <SBGameLines event={e} onPick={() => navigate(primaryTo)} isPicked={() => false}
                        compact header={false} scores={e.status === "live"} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-[#27272A] bg-[#0F0F12] px-3 py-6 text-center text-sm text-[#a1a1aa]">
                  Live lines load here as games hit the board.
                </div>
              )}
            </div>
            <Link to={primaryTo}><Button className="mt-5 rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">Explore sportsbook <Icon name="arrow-right" size={16} className="ml-1" /></Button></Link>
          </motion.div>

          <motion.div {...fadeUp} className="rounded-2xl border border-[#27272A] bg-[#141417] p-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#a1a1aa]">Originals / Casino</p>
            <h3 className="mt-2 font-display text-xl font-black tracking-tight">Instant games. Transparent outcomes.</h3>
            <p className="mt-2 text-sm text-[#a1a1aa]">Eight provably-fair originals plus live-dealer tables — jump in for instant action and verify every outcome yourself.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {ORIGINALS.map((o) => (
                <span key={o.name} className="rounded-full border border-[#27272A] bg-[#0F0F12] px-3 py-1.5 text-xs font-semibold text-[#a1a1aa]">{o.name}</span>
              ))}
            </div>
            <Link to={user ? "/casino" : "/login?mode=register"}><Button className="mt-5 rounded-full bg-[#00D632] font-bold text-[#0A0A0A] hover:bg-[#00B829]">Explore originals <Icon name="arrow-right" size={16} className="ml-1" /></Button></Link>
          </motion.div>
        </div>
      </section>

      {/* 02 Originals detail */}
      <section id="originals" className="border-t border-white/10 bg-[#0C0C0E]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <motion.div {...fadeUp}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#00D632]">02 / Originals</p>
            <h2 className="max-w-2xl font-display text-3xl font-black tracking-tight sm:text-4xl">Casino originals without the visual noise.</h2>
            <p className="mt-4 max-w-2xl text-base text-[#a1a1aa]">Fast games should feel direct. Each one gets a clean identity, a clear mechanic, and a verifiable outcome — not a wall of neon thumbnails.</p>
          </motion.div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {ORIGINALS.map((o, i) => {
              const iconName = o.icon;
              return (
                <motion.div key={o.name} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="group rounded-2xl border border-[#27272A] bg-[#141417] p-6 transition-colors hover:border-[#00D632]/50">
                  <Icon name={iconName} size={32} className="text-[#00D632]" />
                  <p className="mt-4 font-display text-lg font-black tracking-tight">{o.name}</p>
                  <p className="mt-1 text-sm text-[#a1a1aa]">{o.sub}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 04 Rewards */}
      <section id="rewards" className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <motion.div {...fadeUp} className="overflow-hidden rounded-3xl border border-[#00D632]/30 bg-surface-3 border border-surface-border p-8 sm:p-12">
          <div className="grid items-center gap-8 lg:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#00D632]">03 / Daily rewards</p>
              <h2 className="font-display text-3xl font-black tracking-tight sm:text-4xl">Start with <span className="text-[#00D632]">500.</span> Come back for more.</h2>
              <p className="mt-4 max-w-lg text-base text-[#a1a1aa]">Create a free account and your first 500 PCoins land immediately. Return each day to claim another free drop and keep playing without a purchase.</p>
              <Link to={primaryTo}><Button data-testid="rewards-cta" className="mt-6 rounded-full bg-[#00D632] px-8 py-6 text-base font-bold text-[#0A0A0A] hover:bg-[#00B829]">{user ? "Claim daily drop" : "Claim 500 free coins"} <Icon name="arrow-right" size={20} className="ml-1" /></Button></Link>
            </div>
            <div className="flex flex-col items-center justify-center rounded-2xl border border-[#27272A] bg-[#0F0F12] py-10">
              <span className="font-display text-7xl font-black text-[#00D632]">500</span>
              <span className="mt-2 text-sm uppercase tracking-[0.2em] text-[#a1a1aa]">free coins on sign-up</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* 05 How it works */}
      <section className="border-t border-white/10 bg-[#0C0C0E]">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <motion.div {...fadeUp}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#00D632]">04 / How it works</p>
            <h2 className="max-w-2xl font-display text-3xl font-black tracking-tight sm:text-4xl">Three steps. Nothing to over-explain.</h2>
          </motion.div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} className="rounded-2xl border border-[#27272A] bg-[#141417] p-6">
                <span className="font-display text-3xl font-black text-[#00D632]/40">{s.n}</span>
                <h3 className="mt-3 font-display text-lg font-black tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-[#a1a1aa]">{s.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 06 FAQ */}
      <section id="faq" className="mx-auto max-w-3xl px-4 py-20 sm:px-6">
        <motion.div {...fadeUp}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#00D632]">05 / FAQ</p>
          <h2 className="font-display text-3xl font-black tracking-tight sm:text-4xl">Good to know before you play.</h2>
          <p className="mt-4 text-base text-[#a1a1aa]">PRYMEBET is built as free-to-play entertainment with sports, esports and provably-fair original games.</p>
        </motion.div>
        <div className="mt-8 space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-[#27272A] bg-[#141417]">
              <button data-testid={`faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                aria-expanded={openFaq === i} aria-controls={`faq-panel-${i}`}
                className="flex w-full items-center justify-between px-5 py-4 text-left">
                <span className="font-semibold">{f.q}</span>
                <Icon name="chevron-down" size={20} className={`shrink-0 text-[#a1a1aa] transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <p id={`faq-panel-${i}`} role="region" className="px-5 pb-4 text-sm text-[#a1a1aa]">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-6">
          <motion.div {...fadeUp}>
            <h2 className="font-display text-3xl font-black tracking-tight sm:text-5xl">The board is open.</h2>
            <p className="mt-4 text-base text-[#a1a1aa]">Claim your free PCoins and choose your first play.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to={primaryTo}><Button data-testid="cta-bottom" className="rounded-full bg-[#00D632] px-10 py-6 text-base font-bold text-[#0A0A0A] hover:bg-[#00B829]">{user ? "Enter the lobby" : "Claim 500 coins"} <Icon name="arrow-right" size={20} className="ml-1" /></Button></Link>
              <a href="#sportsbook"><Button variant="outline" className="rounded-full border-white/20 bg-white/5 px-10 py-6 text-base font-bold text-white hover:bg-[#00B829]/10 hover:text-white">Explore sportsbook</Button></a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0C0C0E]">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-4 py-10 text-center sm:px-6">
          <img src={logoStacked} alt="PRYMEBET" className="h-[125px] w-auto sm:h-[150px]" />
          <p className="max-w-xl text-xs text-[#a1a1aa]">Free-to-play social casino entertainment for eligible players 18+. No purchase necessary. Play responsibly.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
            <Link to="/terms" data-testid="footer-terms-link" className="text-xs text-[#a1a1aa] underline underline-offset-2 hover:text-white">Terms of Service</Link>
            <Link to="/sweeps-rules" data-testid="footer-sweeps-link" className="text-xs text-[#a1a1aa] underline underline-offset-2 hover:text-white">Sweepstakes Rules</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-xs text-[#a1a1aa] underline underline-offset-2 hover:text-white">Privacy Policy</Link>
            <Link to="/responsible-play" data-testid="footer-responsible-link" className="text-xs text-[#a1a1aa] underline underline-offset-2 hover:text-white">Responsible Play</Link>
            <button type="button" onClick={openCookiePreferences} data-testid="footer-cookie-prefs" className="text-xs text-[#a1a1aa] underline underline-offset-2 hover:text-white">Cookie Preferences</button>
            <Link to="/legal" data-testid="footer-legal-link" className="text-xs font-semibold text-[#00D632] underline underline-offset-2 hover:text-white">Legal &amp; Policies</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
